/**
 * Render-quality profile for the Giro3D scene.
 *
 * On a host with no GPU the browser falls back to a software rasteriser
 * (SwiftShader over Microsoft Basic Render Driver, typical of Hyper-V / RDP
 * VMs). WebGL still works there, so 3D is worth rendering — but every fragment
 * is shaded on the CPU, so the defaults tuned for a GPU (MSAA, HiDPI pixel
 * ratio, a full-screen outline pass, full-resolution tiles, a 50 M point
 * budget) turn an orbit into a slideshow.
 *
 * This module holds the two profiles as pure data so the trade-offs are visible
 * in one place and testable without a WebGL context.
 */

import { isSoftwareWebGlSession } from '@/utils/webglCapability';

export interface RenderQualityProfile {
  readonly id: 'full' | 'reduced';
  /** MSAA at context creation. Costs a multiple of the fill rate in software. */
  readonly antialias: boolean;
  /** Backing-store scale. Below 1 the browser upscales — blurrier but far cheaper. */
  readonly pixelRatio: number;
  /** Selection outline is a second full-screen pass every frame. */
  readonly outlinePass: boolean;
  /** Giro3D `ColorLayer.resolutionFactor` — below 1 requests coarser, fewer tiles. */
  readonly rasterResolutionFactor: number;
  /** Upper bound for the point-cloud budget, whatever the scene asks for. */
  readonly maxPointBudget: number;
  /** Sky / clouds / rain / stars. Full-screen shader work with no data value. */
  readonly environmentEffects: boolean;
}

export const FULL_QUALITY_PROFILE: RenderQualityProfile = {
  id: 'full',
  antialias: true,
  pixelRatio: 1,
  outlinePass: true,
  rasterResolutionFactor: 1,
  maxPointBudget: Number.POSITIVE_INFINITY,
  environmentEffects: true,
};

export const REDUCED_QUALITY_PROFILE: RenderQualityProfile = {
  id: 'reduced',
  antialias: false,
  // Fragment count scales with the square of this, so 0.75 removes ~44% of the
  // shading work the CPU has to do per frame.
  pixelRatio: 0.75,
  outlinePass: false,
  rasterResolutionFactor: 0.5,
  maxPointBudget: 2_000_000,
  environmentEffects: false,
};

/** HiDPI is capped at 2 even on a GPU — beyond that the cost buys nothing. */
const MAX_HARDWARE_PIXEL_RATIO = 2;

export function renderQualityProfileFor(input: {
  readonly softwareRendering: boolean;
  readonly devicePixelRatio?: number;
}): RenderQualityProfile {
  if (input.softwareRendering) return REDUCED_QUALITY_PROFILE;
  const dpr = input.devicePixelRatio;
  const pixelRatio = typeof dpr === 'number' && Number.isFinite(dpr) && dpr > 0
    ? Math.min(dpr, MAX_HARDWARE_PIXEL_RATIO)
    : 1;
  return { ...FULL_QUALITY_PROFILE, pixelRatio };
}

/**
 * The profile for this session. Memoised alongside the WebGL probe: the verdict
 * cannot change without a reload, and callers ask on every layer build.
 */
let cached: RenderQualityProfile | null = null;

export function activeRenderQualityProfile(): RenderQualityProfile {
  cached ??= renderQualityProfileFor({
    softwareRendering: isSoftwareWebGlSession(),
    devicePixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio : undefined,
  });
  return cached;
}

/** Test seam — drop the memoised verdict. */
export function resetRenderQualityProfileCache(): void {
  cached = null;
}

/** Clamp a requested point budget to what this session can actually draw. */
export function capPointBudget(
  budget: number | null | undefined,
  profile: RenderQualityProfile,
): number | null {
  if (typeof budget !== 'number' || !Number.isFinite(budget)) return null;
  return Math.min(budget, profile.maxPointBudget);
}
